import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Check, Clock, CalendarClock, AlarmClock, CalendarDays } from "lucide-react";
import api from "../lib/api";
import { PageHeader, Loading, Empty, Pill, Stamp } from "../components/kit";
import { Button } from "../components/ui/button";
import { TaskModal } from "../components/actions";
import { toast } from "sonner";

const PRIO = { urgent: "bg-red-50 text-red-600 border-red-200", high: "bg-amber-50 text-amber-700 border-amber-200", medium: "bg-sky-50 text-sky-700 border-sky-200", low: "bg-slate-50 text-slate-500 border-slate-200" };

export default function Tasks() {
  const [tasks, setTasks] = useState(null);
  const [modal, setModal] = useState(false);
  const [filter, setFilter] = useState("open");
  const nav = useNavigate();

  const load = useCallback(async () => { const { data } = await api.get("/tasks?limit=500&sort=due_date"); setTasks(data.items); }, []);
  useEffect(() => { load(); }, [load]);

  const complete = async (t) => { await api.put(`/tasks/${t.id}`, { status: t.status === "completed" ? "open" : "completed" }); load(); };
  const snooze = async (t) => { const d = new Date(); d.setDate(d.getDate() + 1); await api.put(`/tasks/${t.id}`, { due_date: d.toISOString().slice(0, 10), status: "open" }); toast.success("Snoozed to tomorrow"); load(); };

  if (!tasks) return <Loading label="Loading tasks" />;
  const today = new Date().toISOString().slice(0, 10);
  const tomorrow = new Date(Date.now() + 864e5).toISOString().slice(0, 10);
  const active = tasks.filter((t) => (filter === "completed" ? t.status === "completed" : t.status !== "completed"));

  const groups = filter === "completed" ? { Completed: active } : {
    Overdue: active.filter((t) => t.due_date < today),
    "Due Today": active.filter((t) => t.due_date === today),
    Tomorrow: active.filter((t) => t.due_date === tomorrow),
    Upcoming: active.filter((t) => t.due_date > tomorrow),
    "No Date": active.filter((t) => !t.due_date),
  };
  const ICONS = { Overdue: AlarmClock, "Due Today": CalendarClock, Tomorrow: CalendarDays, Upcoming: Clock, "No Date": Clock, Completed: Check };

  return (
    <div className="animate-fade-up">
      <PageHeader title="Tasks & Follow-Ups" subtitle="Never let a follow-up slip. Complete, snooze, or reschedule in one click." testid="tasks-header"
        actions={<Button className="bg-navy-800 hover:bg-navy-700 text-white" onClick={() => setModal(true)} data-testid="new-task-btn"><Plus className="w-4 h-4 mr-1.5" />New Task</Button>} />

      <div className="flex gap-2 mb-6">
        {["open", "completed"].map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`task-filter-${f}`} className={`px-4 py-1.5 rounded-lg text-sm font-medium capitalize transition-colors ${filter === f ? "bg-navy-800 text-white" : "bg-white border border-slate-200 text-slate-600"}`}>{f}</button>
        ))}
      </div>

      <div className="space-y-6">
        {Object.entries(groups).map(([label, list]) => list.length === 0 ? null : (
          <div key={label} data-testid={`task-group-${label.toLowerCase().replace(/ /g, "-")}`}>
            <div className="flex items-center gap-2 mb-2">
              {(() => { const I = ICONS[label]; return <I className={`w-4 h-4 ${label === "Overdue" ? "text-red-500" : "text-slate-400"}`} />; })()}
              <h3 className={`font-display font-semibold text-sm ${label === "Overdue" ? "text-red-600" : "text-navy-800"}`}>{label}</h3>
              <span className="text-xs text-slate-400">({list.length})</span>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm divide-y divide-slate-100">
              {list.map((t) => (
                <div key={t.id} className="flex items-center gap-3 px-4 py-3" data-testid={`task-item-${t.id}`}>
                  <button onClick={() => complete(t)} className={`w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 ${t.status === "completed" ? "bg-emerald-500 border-emerald-500" : "border-slate-300 hover:border-gold"}`} data-testid={`toggle-task-${t.id}`}>
                    {t.status === "completed" && <Check className="w-3 h-3 text-white" />}
                  </button>
                  <div className="flex-1 min-w-0 cursor-pointer" onClick={() => t.contact_id && nav(`/contacts/${t.contact_id}`)}>
                    <p className={`text-sm ${t.status === "completed" ? "line-through text-slate-400" : "text-navy-800 font-medium"}`}>{t.title}</p>
                    <p className="text-xs text-slate-400">{t.task_type}{t.due_date ? ` · ${t.due_date}` : ""}</p>
                    <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1">
                      <Stamp prefix={t.automated ? "Auto-created for" : "Created by"} by={t.created_by_name || "System"} at={t.created_at} testid={`task-stamp-${t.id}`} />
                      {t.status === "completed" && <Stamp prefix="Completed by" by={t.completed_by_name || t.updated_by_name} at={t.completed_at || t.updated_at} icon={Check} />}
                    </div>
                  </div>
                  <Pill className={PRIO[t.priority] || PRIO.medium}>{t.priority}</Pill>
                  {t.status !== "completed" && <button onClick={() => snooze(t)} className="text-xs text-slate-400 hover:text-gold-dark" data-testid={`snooze-task-${t.id}`}>Snooze</button>}
                </div>
              ))}
            </div>
          </div>
        ))}
        {active.length === 0 && <Empty title="No tasks here" subtitle="You're all caught up." />}
      </div>

      <TaskModal open={modal} onClose={() => { setModal(false); load(); }} onSaved={load} />
    </div>
  );
}
